import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DevNav, StatusPill } from "@/components/developer/DevNav";
import { Credentials } from "@/components/developer/Credentials";
import { Team } from "@/components/developer/Team";
import { Webhooks } from "@/components/developer/Webhooks";
import { ScopePicker } from "@/pages/developers/DevApps";

const FIELDS = ["name", "description", "website_url", "intended_use", "contact_email"];

function Settings({ app, meta, onDone }) {
  const owner = app.my_role === "owner";
  const [f, setF] = useState(null);
  useEffect(() => {
    setF({ ...Object.fromEntries(FIELDS.map((k) => [k, app[k] || ""])), scopes: app.scopes, app_type: app.app_type,
      redirect_uris: (app.redirect_uris || []).join("\n"), allowed_origins: (app.allowed_origins || []).join("\n") });
  }, [app]);
  if (!f) return null;
  const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...Object.fromEntries(FIELDS.map((k) => [k, f[k] || (["website_url", "contact_email"].includes(k) ? null : "")])), redirect_uris: lines(f.redirect_uris), allowed_origins: lines(f.allowed_origins) };
    if (owner) Object.assign(body, { scopes: f.scopes, app_type: f.app_type });
    try { await api.patch(`/developer/apps/${app.app_id}`, body); toast.success("Saved"); onDone(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const readOnly = app.my_role === "viewer" || ["suspended", "revoked"].includes(app.status);
  return (
    <form className="panel grid gap-3 p-6" onSubmit={save} data-testid="dev-settings-form">
      <p className="eyebrow">Settings</p>
      <fieldset disabled={readOnly} className="grid gap-3">
        {FIELDS.map((k) => <label key={k} className="grid gap-1 text-xs text-slate-400">{k.replace("_", " ")}
          <input className="field" value={f[k]} onChange={set(k)} data-testid={`dev-settings-${k}`} /></label>)}
        <label className="grid gap-1 text-xs text-slate-400">Type
          <select className="field" value={f.app_type} onChange={set("app_type")} disabled={!owner} data-testid="dev-settings-type">
            <option value="personal">Personal script</option><option value="server">Server app (confidential)</option><option value="browser">Browser / mobile (public, PKCE)</option></select></label>
        {f.app_type !== "personal" && <label className="grid gap-1 text-xs text-slate-400">Redirect URIs (one per line, exact match; https, or http://localhost for development; no wildcards)
          <textarea className="field font-mono" rows={3} value={f.redirect_uris} onChange={set("redirect_uris")} data-testid="dev-settings-redirects" /></label>}
        <label className="grid gap-1 text-xs text-slate-400">Allowed browser origins (CORS, one per line)
          <textarea className="field font-mono" rows={2} value={f.allowed_origins} onChange={set("allowed_origins")} data-testid="dev-settings-origins" /></label>
        <ScopePicker all={meta.scopes} risky={meta.high_risk_scopes} value={f.scopes} appType={f.app_type} disabled={!owner} onChange={(scopes) => setF({ ...f, scopes })} />
        {!readOnly && <button className="btn-gold justify-self-start" data-testid="dev-settings-save">Save settings</button>}
      </fieldset>
    </form>
  );
}

function ReviewNotice({ app }) {
  const r = app.review || {};
  if (app.app_type === "personal" || r.status === "not_required") return null;
  return (
    <div className="panel border-orange-700/50 p-4 text-sm text-slate-300" data-testid="dev-review-notice">
      <b className="text-copper-300">OAuth scope review: {r.status}</b>
      {r.requested?.length > 0 && <p>Awaiting staff approval: <span className="font-mono text-xs">{r.requested.join(", ")}</span>. Read scopes work for connected users right away.</p>}
      {r.note && <p className="text-slate-400">Staff note: {r.note}</p>}
      <p className="text-slate-400">Users can currently grant: <span className="font-mono text-xs">{app.delegable_scopes.join(", ") || "nothing yet"}</span></p>
    </div>
  );
}

export default function DevAppDetail() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: app, error } = useQuery({ queryKey: ["dev-app", appId], retry: false, queryFn: () => api.get(`/developer/apps/${appId}`).then((r) => r.data) });
  const { data: meta } = useQuery({ queryKey: ["dev-apps"], queryFn: () => api.get("/developer/apps").then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["dev-app", appId] }); qc.invalidateQueries({ queryKey: ["dev-apps"] }); };
  if (error) return <div className="container-x py-16 text-slate-400" data-testid="dev-app-not-found">Application not found.</div>;
  if (!app || !meta) return <div className="container-x py-16 text-slate-500">Loading…</div>;
  const act = async (fn, msg, after) => { try { await fn(); toast.success(msg); after ? after() : refresh(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="container-x space-y-6 py-12" data-testid="dev-app-detail-page">
      <DevNav />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl text-amber-100 sm:text-4xl" data-testid="dev-app-title">{app.name}</h1>
        <StatusPill status={app.status} testid="dev-app-status" />
        <span className="font-mono text-xs text-slate-500">client_id <span className="break-all text-slate-300" data-testid="dev-app-client-id">{app.client_id}</span> · you are {app.my_role}</span>
        <div className="ml-auto flex gap-2">
          {app.status === "draft" && app.my_role === "owner" && <button className="btn-gold" onClick={() => act(() => api.post(`/developer/apps/${app.app_id}/activate`), "Application activated")} data-testid="dev-app-activate">Activate</button>}
          <Link to={`/developers/usage?app=${app.app_id}`} className="btn-outline" data-testid="dev-app-usage-link">Usage</Link>
          {app.my_role === "owner" && <button className="btn-outline text-red-300" onClick={() => window.confirm("Delete this application? All credentials, tokens and user connections are revoked.") && act(() => api.delete(`/developer/apps/${app.app_id}`), "Deleted", () => navigate("/developers/apps"))} data-testid="dev-app-delete">Delete</button>}
        </div>
      </div>
      {app.status_reason && app.status !== "active" && <p className="text-sm text-copper-300" data-testid="dev-app-status-reason">Staff note: {app.status_reason}</p>}
      <ReviewNotice app={app} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Settings app={app} meta={meta} onDone={refresh} />
        <div className="min-w-0 space-y-6">
          <Team app={app} onDone={refresh} />
        </div>
      </div>
      <Credentials app={app} canManage={app.my_role !== "viewer"} onDone={refresh} />
      <Webhooks app={app} canManage={app.my_role !== "viewer"} />
    </div>
  );
}
